import { useQuery, useMutation } from "@tanstack/react-query"
import {
  listRoles,
  listInvitations,
  listMembers,
  inviteTeamMember,
  resendInvitation,
  cancelInvitation,
  removeTeamMember,
  validateInvitation,
} from "@/lib/http/team"
import { Role, Invitation, TeamMember, InviteTeamMemberInput, InviteTeamMemberResponse } from "@/types/api"
import { setAuth } from "@/lib/store/auth"
import { useNavigate } from "react-router-dom"
import { useDispatch } from "react-redux"
import { AppDispatch } from "@/lib/store"

export const useCheckPendingInvitations = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()

  return async (email: string, authData?: { token: string; user: any }) => {
    // This would need to be implemented as a backend endpoint that checks for invitations by email
    // For now, we'll check localStorage for any pending invitations
    const pendingInvitation = localStorage.getItem("pendingInvitation")
    if (pendingInvitation) {
      const validation = await validateInvitation(pendingInvitation)
      if (validation.valid && validation.email === email) {
        localStorage.removeItem("pendingInvitation")

        // If we have auth data, authenticate the user first
        if (authData) {
          dispatch(setAuth({ token: authData.token, user: authData.user }))
        }

        navigate(`/invite/${pendingInvitation}`, { replace: true })
        return true
      } else {
        localStorage.removeItem("pendingInvitation")
      }
    }
    return false
  }
}

export const useListRoles = () => {
  return useQuery<Role[]>({
    queryKey: ["team", "roles"],
    queryFn: listRoles,
    staleTime: 5 * 60 * 1000, // 5 minutes
  })
}

export const useListInvitations = () => {
  return useQuery<Invitation[]>({
    queryKey: ["team", "invitations"],
    queryFn: listInvitations,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export const useListMembers = () => {
  return useQuery<TeamMember[]>({
    queryKey: ["team", "members"],
    queryFn: listMembers,
    staleTime: 30 * 1000, // 30 seconds
  })
}

export const useInviteTeamMember = () => {
  return useMutation<InviteTeamMemberResponse, Error, InviteTeamMemberInput>({
    mutationFn: inviteTeamMember,
  })
}

export const useResendInvitation = () => {
  return useMutation<{ message: string }, Error, string>({
    mutationFn: resendInvitation,
  })
}

export const useCancelInvitation = () => {
  return useMutation<{ message: string }, Error, string>({
    mutationFn: cancelInvitation,
  })
}

export const useRemoveTeamMember = () => {
  return useMutation<{ success: boolean; message: string }, Error, string>({
    mutationFn: removeTeamMember,
  })
}
